import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type Share = { id: string; church: { id: string; name: string }; kind: string; title: string; description: string | null; happensOn: string | null; link: string | null; mine: boolean };
type Network = { id: string; name: string; kind: string; description: string | null; churches: { id: string; name: string; slug: string }[]; shares: Share[] };
const KIND: Record<string, string> = { asociacion: 'Asociación', concilio: 'Concilio', distrito: 'Distrito', fraternidad: 'Fraternidad', red: 'Red' };
const SHARE: Record<string, string> = { evento: 'Evento', campamento: 'Campamento', curso: 'Curso', recurso: 'Recurso', documento: 'Documento', aviso: 'Aviso' };

/** Red de iglesias: asociaciones/concilios/distritos y lo que cada iglesia comparte. */
export function NetworkPage() {
  const me = useMe();
  const base = `/multichurch/churches/${me.activeChurchId}/networks`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canManage: boolean; networks: Network[] }>(base, signal) : Promise.resolve(null)), [base]);
  const catalog = useAsync((signal) => (me.can('church.manage') || me.isPlatformAdmin ? api.get<{ items: { id: string; name: string; kind: string; churches: number }[] }>('/multichurch/networks', signal).then((r) => r.items) : Promise.resolve([])), [me.personId]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      catalog.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const mine = data.status === 'ready' && data.data ? data.data.networks : [];
  const joinable = catalog.status === 'ready' ? catalog.data.filter((n) => !mine.some((m) => m.id === n.id)) : [];
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Red de iglesias</h1>
        <p className="muted">Asociaciones, concilios y distritos. Cada iglesia comparte lo que quiere; sus datos siguen siendo solo suyos.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && mine.length === 0 ? <Card><EmptyState title="Tu iglesia todavía no pertenece a ninguna red." /></Card> : null}
      {mine.map((n) => (
        <Card key={n.id} title={`${n.name}`} action={<span className="small muted">{KIND[n.kind] ?? n.kind}</span>}>
          {n.description ? <p className="muted">{n.description}</p> : null}
          <p className="small"><strong>Iglesias:</strong> {n.churches.map((c, i) => <span key={c.id}>{i ? ', ' : ''}<a href={`/i/${c.slug}`} target="_blank" rel="noreferrer">{c.name}</a></span>)}</p>
          {data.status === 'ready' && data.data?.canManage ? (
            <form className="form" onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const t = (k: string) => String(f.get(k) ?? '').trim() || null;
              void run(() => api.post(`${base}/${n.id}/shares`, { kind: t('kind'), title: t('title'), description: t('description'), happensOn: t('date'), link: t('link') }), 'Compartido con la red.').then((ok) => ok && form.reset());
            }}>
              <h3 className="small">Compartir con la red</h3>
              <div className="form__row">
                <div className="field"><label htmlFor={`sk-${n.id}`}>Qué</label><select id={`sk-${n.id}`} name="kind">{Object.entries(SHARE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
                <Field label="Título" name="title" required minLength={3} maxLength={150} />
              </div>
              <div className="form__row">
                <Field label="Fecha (opcional)" name="date" type="date" />
                <Field label="Enlace (opcional)" name="link" maxLength={500} placeholder="https://… o /e/…" />
              </div>
              <Field label="Descripción" name="description" maxLength={2000} />
              <Button type="submit" variant="secondary" busy={busy}>Compartir</Button>
            </form>
          ) : null}
          <ul className="list">
            {n.shares.length === 0 ? <li className="small muted">Nada compartido todavía.</li> : null}
            {n.shares.map((s) => (
              <li key={s.id} className="list__item list__item--stack">
                <span>
                  <strong>{SHARE[s.kind]}: {s.title}</strong>
                  <span className="small muted"> · {s.church.name}{s.happensOn ? ` · ${new Date(`${s.happensOn}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</span>
                </span>
                {s.description ? <span className="small pre-line">{s.description}</span> : null}
                <div className="actions">
                  {s.link ? (s.link.startsWith('/') ? <Link to={s.link}>Abrir</Link> : <a href={s.link} target="_blank" rel="noreferrer">Abrir</a>) : null}
                  {s.mine && data.status === 'ready' && data.data?.canManage ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Quitar esto de la red? Las otras iglesias ya no lo verán.')) void run(() => api.delete(`/multichurch/churches/${me.activeChurchId}/network-shares/${s.id}`), 'Quitado.'); }}>Quitar</Button> : null}
                </div>
              </li>
            ))}
          </ul>
          {me.can('church.manage') ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`¿Salir de ${n.name}?`)) void run(() => api.put(`${base}/${n.id}`, { on: false }), 'Tu iglesia salió de la red.'); }}>Salir de la red</Button> : null}
        </Card>
      ))}
      {me.can('church.manage') && joinable.length ? (
        <Card title="Unir mi iglesia a una red">
          <ul className="list">
            {joinable.map((n) => (
              <li key={n.id} className="list__item">
                <span>{n.name} <span className="small muted">· {KIND[n.kind] ?? n.kind} · {n.churches} iglesias</span></span>
                <Button variant="secondary" busy={busy} onClick={() => void run(() => api.put(`${base}/${n.id}`, { on: true }), `Tu iglesia ya es parte de ${n.name}.`)}>Unirme</Button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
